// Layer thumbnails (docs/_wip/layer-thumbnails-2026-10-13.md「サムネ」).
//
// Two pieces, both pure (no DOM / Worker), shared by the worker and the inline
// (main-thread) path:
//
//   - `ThumbnailBuilder`  — the downscale itself. One small RGBA per layer,
//     rebuilt cell by cell, only the cells a dirty bbox touches. Colour =
//     α-weighted mean, α = the MAX α in the cell (so a 2-3 px line that would
//     average to ~5% α at 1/37 scale still shows). Always the real (100%) pixel
//     values: 👁 and display opacity are never consulted.
//   - `ThumbnailScheduler` — WHEN it may run. The whole point is the 描き味 promise:
//     nothing runs while the user's own pen is down, nothing starts until 500 ms
//     after pen-up, and every run is a ≤ 1 ms slice followed by a yield to the
//     message queue (a pen-down can only ever wait for ONE slice).
/** Default thumbnail long edge in pixels (layer-thumbnails-2026-10-13.md). */
export const DEFAULT_THUMBNAIL_LONG_EDGE = 72;
export const THUMBNAIL_TIER_PARAMS = {
    normal: { quietMs: 500, sliceMs: 1, followRemote: true, longEdge: DEFAULT_THUMBNAIL_LONG_EDGE },
    slow: { quietMs: 2000, sliceMs: 0.5, followRemote: false, longEdge: DEFAULT_THUMBNAIL_LONG_EDGE },
    'very-slow': { quietMs: 5000, sliceMs: 0.5, followRemote: false, longEdge: 36 },
};
const TIER_ORDER = ['normal', 'slow', 'very-slow'];
/**
 * Down-shift thresholds per tier (any one trips it). Up-shift needs every
 * metric below `UP_FACTOR` × the CURRENT tier's threshold for `UP_HOLD_MS`
 * (fast down, slow up; no flapping).
 *   overrun = share of recent slices that ran > 1.5 × their budget
 *   lateMs  = EMA of how late the scheduler's own timer fired (worker congestion:
 *             how long a queued task waits before the worker gets to it)
 *   jank    = share of main-thread rAF frames > 48 ms apart (reported by main,
 *             only counting frames while the user is not drawing)
 */
export const THUMBNAIL_TIER_THRESHOLDS = {
    slow: { overrun: 0.3, lateMs: 60, jank: 0.2 },
    'very-slow': { overrun: 0.6, lateMs: 200, jank: 0.5 },
};
/**
 * Last resort (作者承認 2026-10-13): while in `very-slow`, these stop the
 * thumbnails altogether (sticky for the tab). `jank` must hold for
 * `STOP_JANK_REPORTS` reports in a row (one bad burst is not enough).
 */
export const THUMBNAIL_STOP_THRESHOLDS = { overrun: 0.8, lateMs: 500, jank: 0.8 };
export const THUMBNAIL_STOP_JANK_REPORTS = 3;
export const THUMBNAIL_UP_FACTOR = 0.5;
export const THUMBNAIL_UP_HOLD_MS = 30_000;
/** Slices needed in the window before `overrun` counts. */
export const THUMBNAIL_MIN_SAMPLES = 10;
const OVERRUN_WINDOW = 20;
/** A slice counts as overrun above this multiple of its budget. */
const OVERRUN_FACTOR = 1.5;
/**
 * Per-layer cell-wise downscaler. Cells are the thumbnail's pixels; each maps to
 * a source rect (`xs[cx] .. xs[cx+1]` × `ys[cy] .. ys[cy+1]`) computed so that
 * source pixel `x` belongs to cell `floor(x * tw / srcW)` exactly.
 */
export class ThumbnailBuilder {
    srcW = 0;
    srcH = 0;
    tw = 0;
    th = 0;
    xs = new Int32Array(0);
    ys = new Int32Array(0);
    pix = [];
    /** Per layer: 1 = this cell needs rebuilding. */
    dirty = [];
    dirtyCount = [];
    /** Per layer: first cell index that may still be dirty. */
    cursor = [];
    /** Per layer: something changed since the last publish. */
    unpublished = [];
    /** Thumbnail size for a source of `srcW × srcH` (aspect kept, long edge ≤ `longEdge`, never upscaled). */
    static sizeFor(srcW, srcH, longEdge) {
        const long = Math.max(srcW, srcH);
        if (long <= 0)
            return { width: 0, height: 0 };
        const scale = Math.min(1, longEdge / long);
        return {
            width: Math.max(1, Math.min(srcW, Math.round(srcW * scale))),
            height: Math.max(1, Math.min(srcH, Math.round(srcH * scale))),
        };
    }
    /** (Re)allocate for a new source size / layer count. Everything starts dirty (= full rebuild). */
    configure(srcW, srcH, layerCount, longEdge) {
        this.srcW = srcW;
        this.srcH = srcH;
        const size = ThumbnailBuilder.sizeFor(srcW, srcH, longEdge);
        this.tw = size.width;
        this.th = size.height;
        this.xs = new Int32Array(this.tw + 1);
        this.ys = new Int32Array(this.th + 1);
        // start(c) = ceil(c * src / t)  ⇒  pixel x is in cell c  ⇔  c = floor(x * t / src).
        for (let c = 0; c <= this.tw; c++)
            this.xs[c] = Math.ceil((c * srcW) / this.tw);
        for (let c = 0; c <= this.th; c++)
            this.ys[c] = Math.ceil((c * srcH) / this.th);
        const cells = this.tw * this.th;
        this.pix = [];
        this.dirty = [];
        this.dirtyCount = [];
        this.cursor = [];
        this.unpublished = [];
        for (let i = 0; i < layerCount; i++) {
            this.pix.push(new Uint8ClampedArray(cells * 4));
            this.dirty.push(new Uint8Array(cells));
            this.dirtyCount.push(0);
            this.cursor.push(0);
            this.unpublished.push(false);
            this.markAll(i);
        }
    }
    get width() {
        return this.tw;
    }
    get height() {
        return this.th;
    }
    /** Mark one layer fully dirty. */
    markAll(layerId) {
        const flags = this.dirty[layerId];
        if (flags === undefined)
            return;
        flags.fill(1);
        this.dirtyCount[layerId] = flags.length;
        this.cursor[layerId] = 0;
        this.unpublished[layerId] = true;
    }
    /** Mark the cells touched by `bbox` (inclusive source px; clamped; `x0 < 0` = empty) dirty. */
    markBbox(layerId, bbox) {
        const flags = this.dirty[layerId];
        if (flags === undefined || bbox.x0 < 0 || this.tw === 0)
            return;
        const x0 = Math.max(0, bbox.x0);
        const y0 = Math.max(0, bbox.y0);
        const x1 = Math.min(this.srcW - 1, bbox.x1);
        const y1 = Math.min(this.srcH - 1, bbox.y1);
        if (x1 < x0 || y1 < y0)
            return;
        const cx0 = Math.floor((x0 * this.tw) / this.srcW);
        const cx1 = Math.floor((x1 * this.tw) / this.srcW);
        const cy0 = Math.floor((y0 * this.th) / this.srcH);
        const cy1 = Math.floor((y1 * this.th) / this.srcH);
        let added = 0;
        for (let cy = cy0; cy <= cy1; cy++) {
            const row = cy * this.tw;
            for (let cx = cx0; cx <= cx1; cx++) {
                if (flags[row + cx] === 0) {
                    flags[row + cx] = 1;
                    added++;
                }
            }
        }
        this.dirtyCount[layerId] = (this.dirtyCount[layerId] ?? 0) + added;
        const first = cy0 * this.tw + cx0;
        if (first < (this.cursor[layerId] ?? 0))
            this.cursor[layerId] = first;
        this.unpublished[layerId] = true;
    }
    /** Any layer still has cells to rebuild or an unpublished result. */
    hasWork() {
        for (let i = 0; i < this.dirtyCount.length; i++) {
            if ((this.dirtyCount[i] ?? 0) > 0 || this.unpublished[i] === true)
                return true;
        }
        return false;
    }
    /** Number of dirty cells for `layerId` (tests). */
    pendingCells(layerId) {
        return this.dirtyCount[layerId] ?? 0;
    }
    /** Current thumbnail pixels of `layerId` (live view, not a copy; tests). */
    peek(layerId) {
        return this.pix[layerId];
    }
    /**
     * Rebuild dirty cells until `budgetMs` has elapsed (checked every few cells),
     * and return every layer whose rebuild finished during this call (fresh
     * copies, safe to transfer). A layer is published only when it has no dirty
     * cells left, so a partial rebuild never reaches the consumer.
     */
    step(layers, budgetMs, now) {
        const done = [];
        const start = now();
        let counter = 0;
        for (let id = 0; id < this.dirty.length; id++) {
            const layer = layers[id];
            const flags = this.dirty[id];
            const out = this.pix[id];
            if (layer === undefined || flags === undefined || out === undefined)
                continue;
            if (layer.width !== this.srcW || layer.height !== this.srcH)
                continue;
            const total = flags.length;
            let idx = this.cursor[id] ?? 0;
            let remaining = this.dirtyCount[id] ?? 0;
            let timedOut = false;
            while (remaining > 0 && idx < total) {
                if (flags[idx] === 0) {
                    idx++;
                    continue;
                }
                this.computeCell(layer.main, idx % this.tw, Math.floor(idx / this.tw), out, idx * 4);
                flags[idx] = 0;
                remaining--;
                idx++;
                if ((++counter & 3) === 0 && now() - start >= budgetMs) {
                    timedOut = true;
                    break;
                }
            }
            this.cursor[id] = idx;
            this.dirtyCount[id] = remaining;
            if (remaining === 0 && this.unpublished[id] === true) {
                this.unpublished[id] = false;
                this.cursor[id] = 0;
                done.push({ layerId: id, width: this.tw, height: this.th, rgba: out.slice() });
            }
            if (timedOut || now() - start >= budgetMs)
                break;
        }
        return done;
    }
    computeCell(src, cx, cy, out, o) {
        const x0 = this.xs[cx] ?? 0;
        const x1 = this.xs[cx + 1] ?? 0;
        const y0 = this.ys[cy] ?? 0;
        const y1 = this.ys[cy + 1] ?? 0;
        const stride = this.srcW * 4;
        let sumA = 0;
        let sumR = 0;
        let sumG = 0;
        let sumB = 0;
        let maxA = 0;
        for (let y = y0; y < y1; y++) {
            let i = y * stride + x0 * 4;
            for (let x = x0; x < x1; x++, i += 4) {
                const a = src[i + 3];
                if (a === 0)
                    continue;
                if (a > maxA)
                    maxA = a;
                sumA += a;
                sumR += src[i] * a;
                sumG += src[i + 1] * a;
                sumB += src[i + 2] * a;
            }
        }
        if (sumA === 0) {
            out[o] = 0;
            out[o + 1] = 0;
            out[o + 2] = 0;
            out[o + 3] = 0;
            return;
        }
        out[o] = Math.round(sumR / sumA);
        out[o + 1] = Math.round(sumG / sumA);
        out[o + 2] = Math.round(sumB / sumA);
        out[o + 3] = maxA;
    }
}
/**
 * Decides when `ThumbnailBuilder.step` may run. Never runs synchronously from
 * `kick()`: work only ever happens from a timer callback, so it can't sit inside
 * the message handler that reported the change.
 *
 * Gate (all must hold, re-checked on every timer fire):
 *   - `visible` (main: a surface that shows thumbnails exists; while false only
 *     the renderer's per-layer dirty bbox keeps accumulating);
 *   - no own stroke in flight;
 *   - not `suspended` (main: "I'm moving the view" - `previewOverlay.ts`
 *     `shouldRegenThumbnail`'s `suspended`);
 *   - the tier's `quietMs` since the last own pen-down/up or resume
 *     (`noteInput`); in the slow tiers also no peer stroke in flight and
 *     `quietMs` since the last peer activity.
 * When the gate is closed nothing is armed; the event that opens it re-kicks.
 */
export class ThumbnailScheduler {
    builder = new ThumbnailBuilder();
    source;
    onThumbnail;
    now;
    setTimer;
    clearTimer;
    timer = null;
    timerArmed = false;
    armedAt = 0;
    armedMs = 0;
    lastInputAt = Number.NEGATIVE_INFINITY;
    lastRemoteAt = Number.NEGATIVE_INFINITY;
    suspended = false;
    visible;
    disposed = false;
    configured = false;
    stats = null;
    // tier state
    tier = 'normal';
    forcedTier = null;
    overrunWindow = [];
    lateEma = null;
    jank = 0;
    calmSince = null;
    jankStreak = 0;
    stopped = false;
    onStopped;
    constructor(opts) {
        this.source = opts.source;
        this.onThumbnail = opts.onThumbnail;
        this.visible = opts.visible ?? true;
        this.onStopped = opts.onStopped;
        this.now = opts.now ?? (() => performance.now());
        this.setTimer = opts.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
        this.clearTimer = opts.clearTimer ?? ((h) => clearTimeout(h));
        this.rebuildAll();
    }
    /** Canvas resized / layers replaced / resolution changed: reallocate and rebuild everything. */
    rebuildAll() {
        if (this.disposed)
            return;
        const layers = this.source.layers();
        const first = layers[0];
        if (first === undefined)
            return;
        this.builder.configure(first.width, first.height, layers.length, this.params().longEdge);
        // Whatever the renderer accumulated is covered by the full rebuild.
        for (let i = 0; i < layers.length; i++)
            this.source.takeLayerDirty(i);
        this.configured = true;
        this.kick();
    }
    getTier() {
        return this.forcedTier ?? this.tier;
    }
    /** Bench / test: pin a tier (`null` = back to automatic). */
    setTierOverride(tier) {
        const before = this.getTier();
        this.forcedTier = tier;
        this.afterTierMaybeChanged(before);
    }
    /** Main thread: share of rAF frames > 48 ms apart over the last window, while the user was not drawing. */
    reportMainJank(ratio) {
        this.jank = ratio;
        this.jankStreak = ratio > THUMBNAIL_STOP_THRESHOLDS.jank ? this.jankStreak + 1 : 0;
        this.evaluateTier();
    }
    /** Own pen down / up (or any other "the user just did something" moment): restart the quiet period. */
    noteInput() {
        this.lastInputAt = this.now();
        this.kick();
    }
    /** A peer's stroke did something. Only matters in the slow tiers (cheap no-op in `normal`). */
    noteRemoteActivity() {
        if (this.getTier() === 'normal')
            return;
        this.lastRemoteAt = this.now();
    }
    setSuspended(suspended) {
        if (this.suspended === suspended)
            return;
        this.suspended = suspended;
        if (!suspended)
            this.lastInputAt = this.now();
        this.kick();
    }
    /** No surface shows thumbnails (false): dirty tracking continues, nothing is built. true: catch up. */
    setVisible(visible) {
        if (this.visible === visible)
            return;
        this.visible = visible;
        this.kick();
    }
    isVisible() {
        return this.visible;
    }
    /** Per-slice durations (ms), recorded only while stats are on (bench). */
    setRecordStats(on) {
        this.stats = on ? [] : null;
    }
    takeStats() {
        const out = this.stats ?? [];
        if (this.stats !== null)
            this.stats = [];
        return out;
    }
    /** Something may have changed - arm a timer if there is work and the gate allows it. */
    kick() {
        if (this.disposed || this.timerArmed || !this.configured)
            return;
        if (!this.builder.hasWork() && !this.source.hasLayerDirty())
            return;
        const wait = this.gateWait();
        if (wait === null)
            return;
        this.arm(wait);
    }
    dispose() {
        this.disposed = true;
        if (this.timerArmed)
            this.clearTimer(this.timer);
        this.timerArmed = false;
    }
    /** Test hook: is a timer pending? */
    isArmed() {
        return this.timerArmed;
    }
    params() {
        return THUMBNAIL_TIER_PARAMS[this.getTier()];
    }
    /** ms until work may start, or null while the gate is closed. */
    gateWait() {
        if (!this.visible || this.suspended || this.source.hasOwnStroke())
            return null;
        const p = this.params();
        let last = this.lastInputAt;
        if (!p.followRemote) {
            if (this.source.hasRemoteStroke())
                return null;
            last = Math.max(last, this.lastRemoteAt);
        }
        const wait = last + p.quietMs - this.now();
        return wait > 0 ? wait : 0;
    }
    arm(ms) {
        this.timerArmed = true;
        this.armedAt = this.now();
        this.armedMs = ms;
        this.timer = this.setTimer(() => this.fire(), ms);
    }
    fire() {
        this.timerArmed = false;
        this.timer = null;
        if (this.disposed)
            return;
        const t0 = this.now();
        const wait = this.gateWait();
        if (wait === null)
            return; // the opening event re-kicks
        if (wait > 0) {
            this.arm(wait);
            return;
        }
        const budget = this.params().sliceMs;
        this.drainDirty();
        const done = this.builder.step(this.source.layers(), budget, this.now);
        const t1 = this.now();
        if (this.stats !== null)
            this.stats.push(t1 - t0);
        for (const thumb of done)
            this.onThumbnail(thumb);
        this.sample(t1 - t0 > budget * OVERRUN_FACTOR, t0 - this.armedAt - this.armedMs);
        // Yield: next slice goes through the event loop (the worker's pending
        // messages run first).
        this.kick();
    }
    drainDirty() {
        if (!this.source.hasLayerDirty())
            return;
        const layers = this.source.layers();
        for (let i = 0; i < layers.length; i++) {
            this.builder.markBbox(i, this.source.takeLayerDirty(i));
        }
    }
    // ─── tier selection (measured) ───────────────────────────────────────────
    sample(overrun, lateMs) {
        this.overrunWindow.push(overrun);
        if (this.overrunWindow.length > OVERRUN_WINDOW)
            this.overrunWindow.shift();
        const late = Math.max(0, lateMs);
        this.lateEma = this.lateEma === null ? late : this.lateEma * 0.7 + late * 0.3;
        this.evaluateTier();
    }
    /** Tier a metric set maps to when every threshold is scaled by `factor`. */
    levelFor(factor) {
        const n = this.overrunWindow.length;
        let over = 0;
        for (const o of this.overrunWindow)
            if (o)
                over++;
        const overrun = n >= THUMBNAIL_MIN_SAMPLES ? over / n : 0;
        const late = this.lateEma ?? 0;
        const trips = (t) => overrun > t.overrun * factor || late > t.lateMs * factor || this.jank > t.jank * factor;
        if (trips(THUMBNAIL_TIER_THRESHOLDS['very-slow']))
            return 'very-slow';
        if (trips(THUMBNAIL_TIER_THRESHOLDS.slow))
            return 'slow';
        return 'normal';
    }
    isStopped() {
        return this.stopped;
    }
    /** Give up for good: no more work, no more timers (the engine remembers it for the tab). */
    stop() {
        if (this.stopped)
            return;
        this.stopped = true;
        this.dispose();
        this.onStopped?.();
    }
    shouldStop() {
        const t = THUMBNAIL_STOP_THRESHOLDS;
        let over = 0;
        for (const o of this.overrunWindow)
            if (o)
                over++;
        const n = this.overrunWindow.length;
        const overrunHit = n >= THUMBNAIL_MIN_SAMPLES && over / n > t.overrun;
        const lateHit = n >= THUMBNAIL_MIN_SAMPLES && (this.lateEma ?? 0) > t.lateMs;
        return overrunHit || lateHit || this.jankStreak >= THUMBNAIL_STOP_JANK_REPORTS;
    }
    evaluateTier() {
        if (this.stopped)
            return;
        if (this.forcedTier !== null)
            return;
        if (this.tier === 'very-slow' && this.shouldStop()) {
            this.stop();
            return;
        }
        const before = this.tier;
        const cur = TIER_ORDER.indexOf(this.tier);
        const want = TIER_ORDER.indexOf(this.levelFor(1));
        if (want > cur) {
            this.tier = TIER_ORDER[want]; // fast down
            this.calmSince = null;
        }
        else {
            // Slow up (one tier at a time): only once the metrics sit below
            // UP_FACTOR x the thresholds for UP_HOLD_MS in a row.
            const calm = TIER_ORDER.indexOf(this.levelFor(THUMBNAIL_UP_FACTOR));
            if (calm < cur) {
                const t = this.now();
                if (this.calmSince === null)
                    this.calmSince = t;
                else if (t - this.calmSince >= THUMBNAIL_UP_HOLD_MS) {
                    this.tier = TIER_ORDER[cur - 1];
                    this.calmSince = null;
                }
            }
            else {
                this.calmSince = null;
            }
        }
        this.afterTierMaybeChanged(before);
    }
    afterTierMaybeChanged(before) {
        if (this.getTier() === before)
            return;
        this.overrunWindow.length = 0;
        this.lateEma = null;
        const longEdge = THUMBNAIL_TIER_PARAMS[before].longEdge;
        if (this.params().longEdge !== longEdge)
            this.rebuildAll();
        else
            this.kick();
    }
}
//# sourceMappingURL=layerThumbnails.js.map